<template>
    <MultiSelect
        ref="select"
        :options="options"
        :value="objectValue || value"
        :disabled="disabled"
        v-bind="multiselectOptions"
        @select="$emit('input', typeof $event == 'object' ? $event.value : $event)"
    />
</template>

<script>
import MultiSelect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';

export default {
    components: {
        MultiSelect
    },

    props: {
        options: {
            type: Array,
            default: () => []
        },
        settings: {
            type: Object,
            default: () => ({})
        },
        value: {
            type: [Object, String, Number],
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        },
        clearOnOptionsChange: {
            type: Boolean,
            default: false
        },
        initialValue: {
            type: [Object, String, Number, Boolean],
            default: null
        }
    },

    computed: {
        multiselectOptions() {
            return {
                showLabels: false,
                searchable: false,
                maxHeight: this.settings.maxHeight,
                ...this.settings
            };
        },
        objectValue() {
            if (typeof this.options[0] === 'object') {
                if (Object.prototype.hasOwnProperty.call(this.settings, 'groupValues')) {
                    let matched = null;

                    this.options.forEach((group) => {
                        const result = group[this.settings.groupValues].find(
                            (option) => option.value === this.value || option.label === this.value
                        );

                        matched = result || matched;
                    });

                    return matched;
                } else {
                    return this.options.find(
                        (option) => option.value === this.value || option.label === this.value
                    );
                }
            } else {
                return null;
            }
        }
    },

    watch: {
        options(newOptions, oldOptions) {
            if (this.clearOnOptionsChange && oldOptions.length) {
                this.$emit('input', undefined);
            }
        }
    },

    mounted() {
        if (this.initialValue) {
            const value =
                typeof this.initialValue === 'object' ? this.initialValue.value : this.initialValue;
            this.$emit('input', value);
        }
    },

    methods: {
        activate() {
            this.$refs.select.activate();
        }
    }
};
</script>

<style scoped>
/* purgecss start ignore */
.multiselect {
    box-sizing: border-box;
    @apply bg-grey-100 p-1 text-color-inherit cursor-pointer;
}

::v-deep.multiselect input {
    @apply bg-transparent;
}

::v-deep .multiselect__input {
    @apply opacity-0;
}

::v-deep .multiselect__select {
    @apply h-full top-0;
}

::v-deep.multiselect--disabled .multiselect__select {
    @apply hidden;
}

::v-deep .multiselect__select:before {
    left: calc(50% - 4px);
    top: calc(50% - 6px);
    @apply inline-block absolute w-2 h-2 m-0 transform -rotate-45 border-color-current text-color-current;
    border-top: none;
    border-right: none;
    border-left-width: 2px;
    border-bottom-width: 2px;
}

::v-deep .multiselect__tags {
    @apply flex items-center pt-0 pl-3 bg-transparent border-0;
}

::v-deep .multiselect__placeholder {
    @apply my-0 pt-0 text-base text-grey-300;
}

::v-deep .multiselect__single {
    @apply my-0 bg-transparent;
}

::v-deep .multiselect__content-wrapper {
    box-shadow: 0 0 40px rgba(0, 0, 0, 0.2);
    @apply left-0 -mt-12 border-0 rounded-none;
}

::v-deep .multiselect__option--selected {
    @apply font-medium bg-white;
}

::v-deep .multiselect__option {
    @apply pr-6 text-blue-500;
}

::v-deep .multiselect__option--highlight {
    @apply bg-grey-100;
}

.--compact.multiselect {
    @apply flex flex-row-reverse justify-between items-center p-0 pr-1 min-h-initial bg-transparent opacity-100;
}

::v-deep.--compact.multiselect .multiselect__placeholder {
    @apply text-grey-200;
}

::v-deep.--compact.multiselect .multiselect__content-wrapper {
    @apply w-40;
    left: calc(50% - theme('spacing.32') / 2);
    transform: translateY(30px);
    max-height: 380px !important;
}

::v-deep.--compact .multiselect__select {
    @apply static flex-shrink-0 h-2 w-2 pt-0 pb-0 px-0 border-b-2 border-l-2 bg-transparent text-color-inherit transform -rotate-45 transition-transform duration-200;
    margin-bottom: 6px;
}

::v-deep.--compact.multiselect--active .multiselect__select {
    @apply rotate-135 translate-y-1;
}

::v-deep.--compact .multiselect__select:before {
    @apply hidden;
}

::v-deep.--compact .multiselect__tags {
    @apply min-h-initial pr-3 pt-0 pl-0;
}

::v-deep.--compact .multiselect__single {
    @apply my-0 pl-0 leading-none text-color-inherit;
}

.--compact-centered.multiselect {
    @apply justify-center;
}

::v-deep.--compact-options-wide.multiselect .multiselect__content-wrapper {
    @apply w-64;
    left: calc(50% - theme('spacing.64') / 2);
}

.multiselect--disabled {
    @apply bg-grey-100 opacity-100;
}

::v-deep.multiselect--disabled .multiselect__select {
    @apply bg-transparent text-grey-200;
}

::v-deep.multiselect--disabled .multiselect__placeholder,
::v-deep.multiselect--disabled .multiselect__single {
    @apply text-grey-200;
}

::v-deep.--tiny .multiselect__single {
    font-size: 14px;
}

::v-deep.--below.multiselect .multiselect__content-wrapper {
    transform: translateY(150px);
}
/* purgecss end ignore */
</style>
